(ns quuppa.routing
  (:require [re-frame.core :as rf :refer [reg-sub reg-event-fx reg-fx]]
            [quuppa.re-frame-utils :as rf-u]
            [reitit.frontend :as rff]
            [reitit.frontend.controllers :as rfc]
            [reitit.frontend.easy :as rfe]
            ["react" :as react]
            goog.Uri
            goog.Uri.QueryData))

(defn apply-subscriptions [match]
  (reduce into [] (map (fn [c]
                         (when-let [f (:subscriptions c)]
                           (f match)))
                       (:controllers (:data match)))))

(defn- pad-same-length [a b]
  (concat a (take (- (count b) (count a)) (repeat nil))))

;; Modified version from Reitit-frontend.
;; Ignores controller :start/:stop(/:subscriptions) function value changes.
;; This is important because the fn value changes every shadow-cljs reload.
#_{:clj-kondo/ignore [:shadowed-var]}
(defn apply-controllers
  "Applies changes between current controllers and
  those previously enabled. Reinitializes controllers whose
  identity has changed."
  [old-controllers new-match]
  (let [new-controllers (mapv (fn [controller]
                                (assoc controller ::rfc/identity (rfc/get-identity controller new-match)))
                              (:controllers (:data new-match)))
        changed-controllers (->> (map (fn [old new]
                                        ;; different controllers, or params changed
                                        (when (not= (dissoc old :start :stop :subscriptions)
                                                    (dissoc new :start :stop :subscriptions))
                                          {:old old, :new new}))
                                      (pad-same-length old-controllers new-controllers)
                                      (pad-same-length new-controllers old-controllers))
                                 (keep identity)
                                 vec)]
    (doseq [controller (reverse (map :old changed-controllers))]
      (rfc/apply-controller controller :stop))
    (doseq [controller (map :new changed-controllers)]
      (rfc/apply-controller controller :start))
    new-controllers))

(defn apply-match [db match  {:keys [ignore-ws-state?]}]
  (let [old-match (::match db)
        enabled? (or (-> db :ws :state :open?)
                     ignore-ws-state?)
        controllers (when enabled?
                      (apply-controllers (:controllers old-match) match))
        ;; TODO: Move subscription definitions to route data directly, outside of controllers.
        subscriptions (when enabled?
                        (apply-subscriptions match))]
    {:db (assoc db ::match (when match
                             (assoc match
                                    :controllers controllers
                                    :subscriptions subscriptions)))
     :fx [(when (not= (seq subscriptions)
                      (seq (:subscriptions old-match)))
            [:dispatch [::apply-subscriptions]])]}))

;;
;; "Dynamic subscriptions"
;; Subscriptions can be added with the hook defined here.
;; They are used in addition to those defined in the route data.
;;

(reg-event-fx
  ::apply-subscriptions
  (fn [{:keys [db]} [_]]
    (let [subscriptions (apply concat (:subscriptions (::match db))
                               (vals (:subscriptions db)))]
      {:fx [[:dispatch [:nav/subscriptions subscriptions]]]})))

(reg-event-fx
  ::add-subs
  (fn [{:keys [db]} [_ id s]]
    {:db (update db :subscriptions assoc id s)
     :fx [[:dispatch [::apply-subscriptions]]]}))

(reg-event-fx
  ::remove-subs
  (fn [{:keys [db]} [_ id]]
    {:db (update db :subscriptions dissoc id)
     :fx [[:dispatch [::apply-subscriptions]]]}))

(defn use-subscriptions [s react-id]
  (react/useEffect (fn []
                     (let [sub-id (name (gensym))]
                       (rf/dispatch [::add-subs sub-id s])
                       (fn []
                         (rf/dispatch [::remove-subs sub-id]))))
                   react-id))

;; Controllers are enabled after WS connection is opened.
(reg-event-fx
  :nav/enable-controllers
  (fn [{:keys [db]} [_ opts]]
    (apply-match db (::match db) opts)))

(reg-event-fx
  :nav/reset-controllers
  (fn [{:keys [db]} [_ opts]]
    (apply-match (dissoc db ::match) (::match db) opts)))

(reg-event-fx
  :nav/navigated
  (fn [{:keys [db]} [_ match opts]]
    (apply-match db match opts)))

(reg-sub
  ::match-root
  (fn [db _]
    (::match db)))

(rf-u/reg-recursive-sub :nav/match [::match-root])

(reg-sub
  :nav/route-name
  :<- [:nav/match :data :name]
  (fn [v _]
    v))

(reg-sub
  :nav/path-params
  :<- [:nav/match :parameters :path]
  (fn [v _]
    v))

;; returns 'applied' query-params, not the ones from the query string
;; applied means that query parameters that have default values are included, even though they would
;; not actually exist in the query string
(reg-sub
  :nav/query-params
  :<- [:nav/match :parameters :query]
  (fn [v _]
    v))

(reg-event-fx
  :nav/push-state
  (fn [_ [_ & args]]
    (apply rfe/push-state args)
    nil))

(reg-fx
  :push-state
  (fn [args]
    (apply rfe/push-state args)))

(defn update-query [f & args]
  (let [current-hash (subs js/window.location.hash 1)
        ^goog.Uri uri (goog.Uri/parse current-hash)
        new-query (apply f (rff/query-params uri) args)]
    (.setQueryData uri (goog.Uri.QueryData/createFromMap (clj->js new-query)))
    (set! js/window.location.hash (str "#"
                                       (when (empty? (.getPath uri)) "/")
                                       (.toString uri)))))

(reg-event-fx
  :nav/update-query
  (fn [_ [_ & args]]
    (apply update-query args)
    nil))

(reg-fx
  :update-query
  (fn [args]
    (apply update-query args)))
